/* =====================================================================================
   Evidence: what a document actually said, and whether we believe it.

   THE QUOTE IS THE POINT, SO THE QUOTE IS THE BIGGEST THING ON THE CARD. Most review screens
   lead with the extracted value and tuck the source into a footnote, which trains the reader to
   decide on the number alone. Here the claim is a caption and the sentence from the document is
   the body text, because the only question worth asking is whether the source really says this.

   THE QUEUE IS MEANT TO BE SHORT. Everything that reaches it tripped a rule that needed a human:
   a contradiction, a weak source establishing a first number, something the model inferred
   rather than read, or a subject we cannot identify. Everything else was accepted with its quote
   and page intact and is one click away under Evidence. An empty queue is success, not an error
   state, and the empty view says so rather than looking broken.

   WHY EACH CARD EXPLAINS ITSELF. "low_tier_first" means nothing to a person opening this screen
   for the first time in three weeks. Each reason is written out in the sentence a colleague
   would use, because a flag you have to decode is a flag you learn to ignore.
   ===================================================================================== */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BACKEND } from '../lib/db';
import { useMarket } from '../components/MarketBar';
import './evidence.css';

interface Claim {
  id: string; claim_type: string; subject_kind: string; subject_name: string;
  value_text: string | null; value_num: number | null; unit: string | null;
  period: string | null; confidence: string; quote: string; page: number | null;
  trigger_reason: string[]; conflict_note: string | null;
  conflicts_value: string | null; conflicts_document: string | null;
  escalate_after: string | null; overdue: boolean;
  document_id: string; document_title: string; publisher: string | null;
  published_on: string | null; source_url: string | null; confidentiality: string;
  source_tier: string; source_rank: number; source_label: string; guidance: string | null;
  assigned_name: string | null;
}
interface Evidence {
  id: string; claim_type: string; subject_name: string; resolved_name: string | null;
  value_text: string | null; confidence: string; quote: string; page: number | null;
  status: string; decided_at: string | null; decided_by_name: string | null;
  document_title: string; publisher: string | null; source_label: string; source_rank: number;
  source_url: string | null;
}
interface Doc {
  id: string; title: string; publisher: string | null; source_tier: string;
  source_label: string; source_rank: number; published_on: string | null;
  confidentiality: string; page_count: number | null; uploaded_by_name: string | null;
  pending: number; accepted: number; rejected: number; claims: number; created_at: string;
}
interface Candidate { kind: string; id: string; name: string; detail: string | null }

/* Written out, because a reason nobody can read is a reason nobody acts on. */
const WHY: Record<string, { short: string; long: string }> = {
  conflict: {
    short: 'Contradicts what we hold',
    long: 'Another source we have already accepted says something different. One of them is wrong, '
        + 'or they are measuring different things.',
  },
  low_tier_first: {
    short: 'First claim, weak source',
    long: 'Nothing we hold contradicts this, because we hold nothing about it. That is exactly how '
        + 'a number from a company describing itself quietly becomes the figure we defend.',
  },
  inferred: {
    short: 'Worked out, not read',
    long: 'The model derived this rather than reading it in a sentence: a total it summed, a share '
        + 'it divided, a value it read off a chart.',
  },
  unresolved: {
    short: 'Cannot tell who this is about',
    long: 'The subject does not match a centre or company we hold. Attach it to the right one, or '
        + 'reject it. It will not create a new record on its own.',
  },
};

const TYPE_LABEL: Record<string, string> = {
  volume: 'Volume', price: 'Price', platform: 'Platform', incumbent: 'Incumbent',
  contract: 'Contract', headcount: 'Headcount', regulatory: 'Regulatory',
  guideline: 'Guideline', coverage: 'Coverage', personnel: 'People', other: 'Other',
};

export function EvidenceView() {
  const [tab, setTab] = useState<'queue' | 'held' | 'sources'>('queue');
  const [queue, setQueue] = useState<Claim[] | null>(null);
  const [held, setHeld] = useState<Evidence[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (BACKEND !== 'supabase') { setQueue([]); return; }
    const { supabase } = await import('../lib/supabase');
    const app = supabase().schema('app');
    const [{ data: q }, { data: e }, { data: d }] = await Promise.all([
      app.from('v_claim_queue').select('*').order('source_rank').order('claim_type'),
      app.from('v_evidence').select('*').order('decided_at', { ascending: false }).limit(300),
      app.from('v_intel_documents').select('*').order('created_at', { ascending: false }),
    ]);
    setQueue((q ?? []) as unknown as Claim[]);
    setHeld((e ?? []) as unknown as Evidence[]);
    setDocs((d ?? []) as unknown as Doc[]);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function decide(id: string, accept: boolean, note?: string) {
    setBusy(id); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('app')
        .rpc('claim_decide', { p_claim: id, p_accept: accept, p_note: note ?? null });
      if (error) throw new Error(error.message);
      await load();
    } catch (err) { setMsg(String((err as Error).message)); }
    finally { setBusy(null); }
  }

  async function resolveAndAccept(id: string, kind: string, subjectId: string) {
    setBusy(id); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('app').rpc('claim_resolve_and_accept',
        { p_claim: id, p_type: kind, p_id: subjectId, p_note: null });
      if (error) throw new Error(error.message);
      await load();
    } catch (err) { setMsg(String((err as Error).message)); }
    finally { setBusy(null); }
  }

  const counts = useMemo(() => ({
    queue: queue?.length ?? 0,
    overdue: (queue ?? []).filter((c) => c.overdue).length,
    held: held.filter((h) => h.status !== 'rejected').length,
  }), [queue, held]);

  if (queue === null) return <div className="ev-wrap"><p className="muted">Loading…</p></div>;

  return (
    <div className="ev-wrap">
      <header className="ev-head">
        <div>
          <h1>Evidence</h1>
          <p className="ev-sub">
            Every figure below carries the sentence it came from and the page it sits on. Nothing
            here was written by a model without a source behind it.
          </p>
        </div>
      </header>

      <nav className="ev-tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'queue'}
                className={tab === 'queue' ? 'on' : ''} onClick={() => setTab('queue')}>
          Waiting on you
          {counts.queue > 0 && <span className="ev-count">{counts.queue}</span>}
          {counts.overdue > 0 && <span className="ev-count od">{counts.overdue} overdue</span>}
        </button>
        <button role="tab" aria-selected={tab === 'held'}
                className={tab === 'held' ? 'on' : ''} onClick={() => setTab('held')}>
          What we hold <span className="ev-count quiet">{counts.held}</span>
        </button>
        <button role="tab" aria-selected={tab === 'sources'}
                className={tab === 'sources' ? 'on' : ''} onClick={() => setTab('sources')}>
          Documents <span className="ev-count quiet">{docs.length}</span>
        </button>
      </nav>

      {msg && <p className="ev-msg">{msg}</p>}

      {tab === 'queue' && (
        queue.length === 0
          ? <EmptyQueue held={counts.held} />
          : <div className="ev-queue">
              {queue.map((c) => (
                <ClaimCard key={c.id} claim={c} busy={busy === c.id}
                           onDecide={decide} onResolve={resolveAndAccept} />
              ))}
            </div>
      )}

      {tab === 'held' && <HeldList rows={held} />}
      {tab === 'sources' && <><Upload onDone={load} /><DocList rows={docs} /></>}
    </div>
  );
}

/* An empty queue is the goal, so it should read like an achievement rather than a void. */
function EmptyQueue({ held }: { held: number }) {
  return (
    <div className="ev-empty">
      <p className="ev-empty-h">Nothing waiting.</p>
      <p>
        {held > 0
          ? <>All {held} claims we hold were either accepted by someone or cleared every rule that
             would have needed a person. They keep their quotes and page numbers either way.</>
          : <>Upload a document and its claims will appear here if any of them need a decision.</>}
      </p>
    </div>
  );
}

function ClaimCard({ claim, busy, onDecide, onResolve }: {
  claim: Claim; busy: boolean;
  onDecide: (id: string, accept: boolean, note?: string) => void;
  onResolve: (id: string, kind: string, subjectId: string) => void;
}) {
  const needsSubject = claim.trigger_reason.includes('unresolved');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Candidate[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!needsSubject || q.trim().length < 2) { setHits([]); return; }
    let live = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { supabase } = await import('../lib/supabase');
        const { data } = await supabase().schema('app')
          .rpc('claim_subject_search', { p_query: q.trim(), p_kind: null });
        if (live) setHits((data ?? []) as unknown as Candidate[]);
      } finally { if (live) setSearching(false); }
    }, 220);
    return () => { live = false; clearTimeout(t); };
  }, [q, needsSubject]);

  return (
    <article className={`card ev-card${claim.overdue ? ' overdue' : ''}`}>
      <div className="ev-card-top">
        <span className={`ev-type t-${claim.claim_type}`}>
          {TYPE_LABEL[claim.claim_type] ?? claim.claim_type}
        </span>
        <h3 className="ev-subject">{claim.subject_name}</h3>
        {claim.overdue && <span className="ev-od" title={`Escalated ${claim.escalate_after}`}>
          Overdue
        </span>}
      </div>

      {claim.value_text && <p className="ev-value">
        {claim.value_text}
        {claim.period && <span className="ev-period"> · {claim.period}</span>}
      </p>}

      {/* The evidence itself. Given the most readable treatment on the card deliberately. */}
      <blockquote className="ev-quote">
        {claim.quote}
        {claim.page != null && <cite>page {claim.page}</cite>}
      </blockquote>

      <p className="ev-source">
        <span className={`ev-tier r${Math.min(claim.source_rank, 8)}`} title={claim.guidance ?? ''}>
          {claim.source_label}
        </span>
        {claim.publisher && <strong>{claim.publisher}</strong>}
        <span className="ev-doc-t">{claim.document_title}</span>
        {claim.source_url && <a href={claim.source_url} target="_blank" rel="noreferrer">source</a>}
      </p>

      <ul className="ev-why">
        {claim.trigger_reason.map((r) => (
          <li key={r}>
            <span className={`ev-flag f-${r}`}>{WHY[r]?.short ?? r}</span>
            <span className="ev-why-long">{WHY[r]?.long ?? ''}</span>
          </li>
        ))}
      </ul>

      {claim.conflict_note && (
        <p className="ev-conflict">
          {claim.conflict_note}
          {claim.conflicts_value && <>
            {' '}Held: <em>{claim.conflicts_value}</em>
            {claim.conflicts_document && <>, from {claim.conflicts_document}.</>}
          </>}
        </p>
      )}

      {needsSubject && (
        <div className="ev-resolve">
          <label htmlFor={`s-${claim.id}`}>Attach to</label>
          <input id={`s-${claim.id}`} value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search centres and companies…" autoComplete="off" />
          {searching && <p className="muted sm">Searching…</p>}
          {hits.length > 0 && (
            <ul className="ev-hits">
              {hits.map((h) => (
                <li key={`${h.kind}-${h.id}`}>
                  <button disabled={busy} onClick={() => onResolve(claim.id, h.kind, h.id)}>
                    <span className={`ev-kind k-${h.kind}`}>{h.kind}</span>
                    <span className="ev-hit-n">{h.name}</span>
                    {h.detail && <span className="ev-hit-d">{h.detail}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {q.trim().length >= 2 && !searching && hits.length === 0 && (
            <p className="ev-none">
              Nothing matches. If this is a real centre or company we do not yet hold, add it in
              Atlas first: this screen will not create one, because a record invented from a
              document is how duplicates start.
            </p>
          )}
        </div>
      )}

      <div className="ev-actions">
        {!needsSubject && (
          <button className="btn primary" disabled={busy}
                  onClick={() => onDecide(claim.id, true)}>Accept</button>
        )}
        <button className="btn" disabled={busy}
                onClick={() => onDecide(claim.id, false)}>Reject</button>
        {claim.confidence !== 'stated' && (
          <span className="ev-conf">{claim.confidence}</span>
        )}
      </div>
    </article>
  );
}

function HeldList({ rows }: { rows: Evidence[] }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) =>
      (r.subject_name + ' ' + (r.resolved_name ?? '') + ' ' + (r.value_text ?? '') + ' ' + r.quote)
        .toLowerCase().includes(n));
  }, [rows, q]);

  if (rows.length === 0) return (
    <p className="ev-empty-h" style={{ marginTop: 28 }}>Nothing held yet.</p>
  );

  return (
    <>
      <input className="ev-search" value={q} onChange={(e) => setQ(e.target.value)}
             placeholder="Search what we hold…" />
      <table className="ev-table">
        <thead><tr>
          <th>Subject</th><th>Claim</th><th>Evidence</th><th>Source</th><th>Feeds</th><th>Decided</th>
        </tr></thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.id} className={r.status === 'rejected' ? 'rej' : ''}>
              <td>
                <strong>{r.resolved_name ?? r.subject_name}</strong>
                {r.resolved_name && r.resolved_name !== r.subject_name &&
                  <span className="ev-as"> written as “{r.subject_name}”</span>}
              </td>
              <td>
                <span className={`ev-type sm t-${r.claim_type}`}>
                  {TYPE_LABEL[r.claim_type] ?? r.claim_type}
                </span>
                <span className="ev-val">{r.value_text}</span>
              </td>
              <td className="ev-q-cell">
                “{r.quote}”
                {r.page != null && <span className="ev-pg"> p{r.page}</span>}
              </td>
              <td>
                <span className={`ev-tier sm r${Math.min(r.source_rank, 8)}`}>{r.source_label}</span>
                <span className="ev-pub">{r.publisher ?? r.document_title}</span>
              </td>
              <td className="ev-promote"><Promote id={r.id} /></td>
              <td className="ev-dec">
                {r.status === 'auto_accepted'
                  ? <span title="Cleared every rule that would have needed a person">automatic</span>
                  : r.status === 'rejected'
                    ? <span className="rejw">rejected</span>
                    : <span>{r.decided_by_name ?? 'accepted'}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DocList({ rows }: { rows: Doc[] }) {
  if (rows.length === 0) return (
    <div className="ev-empty">
      <p className="ev-empty-h">No documents yet.</p>
      <p>
        Documents are read with the extractor, which pulls out every claim it can evidence with a
        verbatim quote and checks each quote against the page before anything is recorded.
      </p>
    </div>
  );
  return (
    <div className="ev-docs">
      {rows.map((d) => (
        <article key={d.id} className="card ev-doc">
          <div className="ev-doc-h">
            <span className={`ev-tier r${Math.min(d.source_rank, 8)}`}>{d.source_label}</span>
            {d.confidentiality === 'restricted' &&
              <span className="ev-conf-flag" title="Visible to the uploader and to owners only">
                restricted
              </span>}
          </div>
          <h3>{d.title}</h3>
          <p className="ev-doc-meta">
            {d.publisher && <>{d.publisher} · </>}
            {d.published_on ?? 'undated'}
            {d.page_count != null && <> · {d.page_count} pages</>}
            {d.uploaded_by_name && <> · added by {d.uploaded_by_name}</>}
          </p>
          <p className="ev-doc-counts">
            <span className="c-held">{d.accepted} held</span>
            {d.pending > 0 && <span className="c-pending">{d.pending} waiting</span>}
            {d.rejected > 0 && <span className="c-rej">{d.rejected} rejected</span>}
          </p>
        </article>
      ))}
    </div>
  );
}

/* Upload. Two fields are compulsory and one of them is the one people want to skip: the source
   tier. Everything downstream leans on it, because it decides whether a first claim from this
   document is taken on trust or put in front of a person, and a document filed as a registry
   return when it is a sales brochure poisons that judgement for everything it touches. */
function Upload({ onDone }: { onDone: () => void }) {
  const { markets, marketId } = useMarket();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [publisher, setPublisher] = useState('');
  const [tier, setTier] = useState('');
  const [published, setPublished] = useState('');
  const [conf, setConf] = useState('restricted');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function pick(f: File | null) {
    setFile(f);
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }

  async function go() {
    if (!file || !title.trim() || !tier) { setErr('A file, a title and a source tier.'); return; }
    setBusy(true); setErr(null); setNote('Uploading…');
    try {
      const { supabase } = await import('../lib/supabase');
      const sb = supabase();
      const { data: org } = await sb.schema('app').rpc('current_org_id');
      const safe = file.name.replace(/[^\w.\- ]+/g, '_');
      const path = `${org}/${Date.now()}-${safe}`;
      const up = await sb.storage.from('intel-documents')
        .upload(path, file, { contentType: file.type || undefined, upsert: false });
      if (up.error) throw new Error(up.error.message);

      setNote('Reading the document. This takes a minute or two for a long one.');
      const { data, error } = await sb.functions.invoke('extract-document', {
        body: {
          path, title: title.trim(), publisher: publisher.trim() || null,
          source_tier: tier, market_id: marketId,
          published_on: published || null, confidentiality: conf,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setNote(`${data.verified} claim(s) verified against the page, ${data.dropped} dropped `
            + `for a quote that was not there. ${data.pending} waiting on you, `
            + `${data.auto_accepted} accepted automatically.`);
      setFile(null); setTitle(''); setPublisher(''); setTier(''); setPublished('');
      await onDone();
    } catch (e) {
      setErr(String((e as Error).message)); setNote(null);
    } finally { setBusy(false); }
  }

  return (
    <section className="card ev-upload">
      <h3>Add a document</h3>
      <p className="ev-upload-sub">
        Every claim it produces is checked against the page it cites before anything is recorded.
        Quotes that are not found are discarded rather than queued.
      </p>
      <div className="ev-form">
        <label className="ev-file">
          <input type="file" accept=".pdf,.txt,.md" disabled={busy}
                 onChange={(e) => pick(e.target.files?.[0] ?? null)} />
          <span>{file ? file.name : 'Choose a PDF or text file…'}</span>
        </label>
        <input placeholder="Title" value={title} disabled={busy}
               onChange={(e) => setTitle(e.target.value)} />
        <input placeholder="Publisher" value={publisher} disabled={busy}
               onChange={(e) => setPublisher(e.target.value)} />
        <select value={tier} disabled={busy} onChange={(e) => setTier(e.target.value)}>
          <option value="">What kind of source is it?</option>
          <option value="registry">Registry or statutory return</option>
          <option value="peer_reviewed">Peer-reviewed publication</option>
          <option value="regulatory">Regulatory filing or determination</option>
          <option value="guideline">Society guideline or position</option>
          <option value="company_filing">Company statutory filing</option>
          <option value="company_material">Company marketing or investor material</option>
          <option value="press">Press or trade coverage</option>
          <option value="personal">Personal communication</option>
        </select>
        <input type="date" value={published} disabled={busy}
               onChange={(e) => setPublished(e.target.value)} title="Published on" />
        <select value={conf} disabled={busy} onChange={(e) => setConf(e.target.value)}>
          <option value="restricted">Restricted: me and owners only</option>
          <option value="internal">Internal: anyone here</option>
          <option value="public">Public: anyone could download it</option>
        </select>
        {markets.length > 1 && (
          <p className="ev-mkt-note">
            Filed under {markets.find((m) => m.id === marketId)?.name ?? 'every market'}.
          </p>
        )}
        <button className="btn primary" disabled={busy || !file} onClick={go}>
          {busy ? 'Working…' : 'Read it'}
        </button>
      </div>
      {note && <p className="ev-note">{note}</p>}
      {err && <p className="ev-msg">{err}</p>}
    </section>
  );
}

/* Promoting a claim into an operational table. Deliberately not a bare button: it asks the
   database first what this claim could do, and shows the answer, because "Promote" next to a
   claim that has nowhere to go is a control that teaches the user the app is lying to them.

   Most claims will say "evidence only" forever, and that is the correct outcome rather than a
   missing feature. A competitor record holds prose and judgements; there is no column for
   "employs more than 100 people" to land in, and appending each accepted claim to the notes
   would turn a considered assessment into a changelog. */
function Promote({ id }: { id: string }) {
  const [state, setState] = useState<{ ok: boolean; reason: string } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (BACKEND !== 'supabase') return;
    let live = true;
    (async () => {
      const { supabase } = await import('../lib/supabase');
      const { data } = await supabase().schema('app').rpc('claim_promotable', { p_claim: id });
      const r = Array.isArray(data) ? data[0] : data;
      if (live && r) setState({ ok: !!r.ok, reason: r.reason ?? '' });
    })();
    return () => { live = false; };
  }, [id]);

  async function go() {
    setBusy(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().schema('app').rpc('claim_promote', { p_claim: id });
      if (error) throw new Error(error.message);
      setDone(String(data ?? 'Written.'));
    } catch (e) { setDone(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  if (done) return <span className="ev-promoted">{done}</span>;
  if (!state) return <span className="ev-quiet">—</span>;
  if (!state.ok) return <span className="ev-quiet" title={state.reason}>evidence only</span>;
  return (
    <button className="btn sm" disabled={busy} onClick={go} title={state.reason}>
      {busy ? '…' : 'Feed into the record'}
    </button>
  );
}
